/**
 * Login / routing / complaint `commented_by_type` use `role_name` from the API:
 * - "Super Admin" → admin area / super_admin
 * - "Backend" → backend
 * - Anything else → employee
 *
 * Accountable departments / locations come as nested objects:
 * `accountable_departments: [{ id, name }]`, `locations: [{ id, name }]`.
 */

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function isSuperAdminRoleType(value) {
  return norm(value).includes('super_admin');
}

/** True only when account Level Type is Level 1 or Level 2. Name and role are ignored. */
export function isLevelOneOrTwoAccount(account) {
  const raw =
    account?.data != null && typeof account.data === 'object' && !Array.isArray(account.data)
      ? account.data
      : account;
  if (!raw || typeof raw !== 'object') return false;
  const compact = norm(raw.level_type ?? raw.levelType).replace(/[\s_-]+/g, '');
  return compact === 'level1' || compact === 'level2';
}

function namesFromIdNameList(list) {
  if (!Array.isArray(list) || !list.length) return [];
  return list
    .map((item) => {
      if (item == null || item === '') return '';
      if (typeof item === 'object') return String(item.name ?? '').trim();
      return String(item).trim();
    })
    .filter(Boolean);
}

function idsFromIdNameList(list) {
  if (!Array.isArray(list) || !list.length) return [];
  return list
    .map((item) => {
      if (item == null || item === '') return '';
      if (typeof item === 'object') {
        const id = item.id ?? item.location_id ?? item.accountable_department_id;
        return id == null || id === '' ? '' : String(id);
      }
      return String(item);
    })
    .filter(Boolean);
}

/**
 * @returns {'super_admin' | 'backend' | 'employee'}
 */
export function classifyByRoleName(roleName = '') {
  const d = norm(roleName);
  if (d.includes('super_admin') || d === 'super admin' || d.includes('super admin')) {
    return 'super_admin';
  }
  if (d === 'backend' || d.includes('backend')) return 'backend';
  return 'employee';
}

/** @deprecated Use classifyByRoleName */
export const classifyByDesignation = classifyByRoleName;

/** Account / login payload: read role label (role tier) */
export function roleNameFromAccount(account) {
  if (!account || typeof account !== 'object') return '';
  const nested =
    typeof account.role === 'object' && account.role?.name
      ? String(account.role.name)
      : '';
  return nested || account.role_name || account.roleName || '';
}

/** Account / login payload: read job designation label (display only) */
export function designationNameFromAccount(account) {
  if (!account || typeof account !== 'object') return '';
  const nested =
    typeof account.designation === 'object' && account.designation?.name
      ? String(account.designation.name)
      : '';
  return nested || account.designation_name || account.designationName || '';
}

/** Accountable department ids from account payload */
export function accountableDepartmentIdsFromAccount(account) {
  if (!account || typeof account !== 'object') return [];
  const fromObjects = idsFromIdNameList(
    account.accountable_departments ?? account.accountableDepartments
  );
  if (fromObjects.length) return fromObjects;
  return idsFromIdNameList(
    account.accountable_department_ids ??
      account.accountableDepartmentIds ??
      account.accountable_department_id ??
      account.accountableDepartmentId
  );
}

/** Location ids from account payload */
export function locationIdsFromAccount(account) {
  if (!account || typeof account !== 'object') return [];
  const fromObjects = idsFromIdNameList(account.locations);
  if (fromObjects.length) return fromObjects;
  return idsFromIdNameList(
    account.location_ids ??
      account.locationIds ??
      account.location_id ??
      account.location?.id
  );
}

/** Comma-separated location labels from account payload */
export function locationNamesFromAccount(account) {
  if (!account || typeof account !== 'object') return '';
  const fromObjects = namesFromIdNameList(account.locations ?? account.location_list);
  if (fromObjects.length) return fromObjects.join(', ');

  const fromNameArrays = namesFromIdNameList(
    account.location_names ?? account.location_name ?? account.locationName
  );
  if (fromNameArrays.length) return fromNameArrays.join(', ');

  const single =
    (typeof account.location === 'object' ? account.location?.name : account.location) ?? '';
  return String(single || '').trim();
}

/** Account / login payload: read department label */
export function departmentNameFromAccount(account) {
  if (!account || typeof account !== 'object') return '';
  const fromAccountable = namesFromIdNameList(
    account.accountable_departments ?? account.accountableDepartments
  );
  if (fromAccountable.length) return fromAccountable.join(', ');

  const nested =
    typeof account.department === 'object' && account.department?.name
      ? String(account.department.name)
      : '';
  if (nested) return nested;

  const fromNameArrays = namesFromIdNameList(
    account.department_name ?? account.departmentName
  );
  if (fromNameArrays.length) return fromNameArrays.join(', ');

  return account.department_name || account.departmentName || '';
}

/** Role tier label: prefer role_name; fall back for older sessions/API */
export function roleTierLabelFromAccount(account) {
  return (
    roleNameFromAccount(account) ||
    designationNameFromAccount(account) ||
    departmentNameFromAccount(account)
  );
}

export function accountableDepartmentLabelFromAccount(account) {
  if (!account || typeof account !== 'object') return '';
  const fromList = namesFromIdNameList(
    account.accountable_departments ?? account.accountableDepartments
  );
  if (fromList.length) return fromList.join(', ');

  const fromNested =
    (typeof account.accountable_department === 'object' &&
      account.accountable_department?.name) ||
    (typeof account.department === 'object' && account.department?.name);
  if (fromNested) return String(fromNested);

  const fromNameArrays = namesFromIdNameList(
    account.department_name ?? account.departmentName
  );
  if (fromNameArrays.length) return fromNameArrays.join(', ');

  return (
    account.accountable_department_name ||
    account.accountableDepartmentName ||
    account.department_name ||
    account.departmentName ||
    ''
  );
}

/** App routing / `area`: `admin` | `backend` | `employee` */
export function inferAuthArea({ roleName, designationName, departmentName, roleType } = {}) {
  if (isSuperAdminRoleType(roleType)) return 'admin';
  const tier = classifyByRoleName(roleName || designationName || departmentName);
  if (tier === 'super_admin') return 'admin';
  if (tier === 'backend') return 'backend';
  return 'employee';
}

/** Route guards / `roleKey`: `admin` | `backend` | `employee` */
export function inferAuthRoleKey({ roleName, designationName, departmentName, roleType } = {}) {
  if (isSuperAdminRoleType(roleType)) return 'admin';
  const tier = classifyByRoleName(roleName || designationName || departmentName);
  if (tier === 'super_admin') return 'admin';
  if (tier === 'backend') return 'backend';
  return 'employee';
}

/** POST /api/complaint_comments `commented_by_type` */
export function inferCommentedByType({ roleName, designationName, departmentName } = {}) {
  return classifyByRoleName(roleName || designationName || departmentName);
}
